import dayjs from 'dayjs';
import get from 'lodash/get';
import { shouldTurnOnSecureAssessment } from 'chameleon-src/utility/helpers';
import { AllAssignments } from 'chameleon-src/utility/utils';
import {
  calculateClassesStats,
  getAssignmentDates,
  getFormIdField,
  getNowMinTimezoneTime,
  getSectionStudents,
  getStudentsBySections,
} from 'chameleon-src/components/assign/AssignManageForm/utils';
import { getFinishedPercent } from 'chameleon-src/components/learnosity/utils/helpers';
import { PartType } from 'chameleon-src/components/assign/AssignManageForm/utils/constants';
import buildSectionStudents from 'chameleon/ui-stack/utilities/utils/buildSectionStudents';

/**
 * Initialize the form with default values.
 * */
const initAssessmentForm = (props) => {
  const {
    isNotPreAPSubjectAndNotPPC,
    assessmentWithQuestionsData,
    hasAudioRecordOrFileUpload,
    assignment,
    isFinalExam,
    individualStudentAssignmentFlag,
    assessments,
    subject: { classes: subjectClasses },
    initialized: wasFormInitialized,
    assignmentStudentsStatusData,
    initialFormStudents,
    assessmentGroupInfo,
    initialFormValues,
    formData,
    dispatchClassesStats,
    classesStats,
    autoSelectedGroups,
    regularSections,
    updateSections,
    progressSections,
    defaultTimezone,
    sectionsMinTimezone,
    disabledStudents,
    isPilot,
    shouldApplyRestriction,
    acceptedPracticeExamToc,
    testTakingLocation,
    initialize,
    setDisabledStudents,
    studentsBySection,
    allowHardTimer,
  } = props;
  const { allAssignees, students: propFormStudents } = formData;
  const sectionId = assignment
    ? Number.parseInt(assignment.assignees[0].id, 10)
    : null;
  const assignmentId = assignment ? assignment.id.toString() : null;

  const isSecureAssessmentMandatory =
    isNotPreAPSubjectAndNotPPC &&
    shouldTurnOnSecureAssessment(
      assessmentWithQuestionsData,
      hasAudioRecordOrFileUpload,
    );

  // if is an update, load initial values
  if (assignment) {
    let allAssigneesList = [];
    let formStudentsWithAllInfo = [];
    if (!isFinalExam && individualStudentAssignmentFlag && !!assessments) {
      /**
       * this logic uses the assessments array to find assignments whose assessment has an ID of
       * assessmentWithQuestionsData.id.
       */
      const assignments = new AllAssignments(
        assessments,
        assessmentWithQuestionsData.id,
      ).getAssignments();
      /**
       * Sometimes assessments will have stale data i.e. the assignment inside
       * it may not have all attributes updated, in the case of this comment the students within the assignment
       * were stale, so what we will do is replace the possibly-stale assignment in the array with the assignment
       * prop that we know is up-to-date
       */
      const assignmentProp = assignment;
      if (assignment) {
        assignments.map((assignment, index) => {
          if (parseInt(assignment.id, 10) === parseInt(assignmentProp.id, 10)) {
            assignments[index] = {
              ...assignmentProp,
              id: String(assignment.id), // we require the assignment ID to be a string for comparisons downstream
            };
          }
        });
      }

      const allStudents = getStudentsBySections(subjectClasses) || [];

      /* The initialization of the data should be done only once. */
      if (!wasFormInitialized) {
        assignments.forEach((assignmentData) => {
          const {
            id: currentAssignmentId,
            section_id,
            section_name,
            sub_group_students,
          } = assignmentData;
          let currentISAStudents = [];
          let currentFullAssignmentStudents = get(
            studentsBySection,
            [parseInt(section_id, 10)],
            [],
          ).map((student) => ({
            id: String(student.id),
            sectionId: parseInt(section_id, 10),
          }));
          if (sub_group_students.length > 0) {
            currentISAStudents = sub_group_students.map((student_id) => ({
              id: String(student_id),
              sectionId: parseInt(section_id, 10),
            }));
            currentFullAssignmentStudents = [];
          }

          const currentAssignmentSectionId = section_id.toString();
          const studentsList = (currentISAStudents || []).concat(
            currentFullAssignmentStudents || [],
          );

          let isFullClass;
          if (studentsList.length > 0) {
            /*
             * Add all the students that are already part of the assignment to redux-form.
             * The student field data within the form shall have the following structure (beside it's own fields):
             * {
             *   id: STUDENT_ID__SECTION_ID__ASSIGNMENT_ID     - this is used for unique identify students within the form
             *   studentId: STUDENT_ID                         - this is the actual ID of the student
             *   sectionId: SECTION_ID                         - this is the section ID that the student belongs to
             *   assignmentId: ASSIGNMENT_ID                   - this is the assignment ID that the student belongs to
             * }
             * */
            /**
             * explicitly casting to string since comparisons downstream requires string type for comparison
             */
            const studentIds = studentsList.map((s) => String(s.id || ''));

            formStudentsWithAllInfo = formStudentsWithAllInfo.concat(
              allStudents
                .filter(
                  (student) =>
                    studentIds.includes(student.studentId) &&
                    student.sectionId === currentAssignmentSectionId,
                )
                .map((student) => ({
                  ...student,
                  id: getFormIdField(
                    student.studentId,
                    student.sectionId,
                    currentAssignmentId,
                  ),
                  studentId: student.studentId,
                  assignmentId: currentAssignmentId,
                  status:
                    assignmentStudentsStatusData[currentAssignmentId] &&
                    assignmentStudentsStatusData[currentAssignmentId][
                      student.studentId
                    ],
                })),
            );

            const sectionStudents = getSectionStudents(
              subjectClasses,
              currentAssignmentSectionId,
            );
            isFullClass = sectionStudents.length === studentsList.length;
          } else {
            /*
             * If there aren't any students part of the assignment add all section's students to the form.
             * If a class is not an ISA, all the students shall be selected by default.
             * The student field data within the form shall have the following structure (beside it's own fields):
             * {
             *   id: STUDENT_ID__SECTION_ID__ASSIGNMENT_ID     - this is used for unique identify students within the form
             *   studentId: STUDENT_ID                         - this is the actual ID of the student
             *   sectionId: SECTION_ID                         - this is the section ID that the student belongs to
             *   assignmentId: ASSIGNMENT_ID                   - this is the assignment ID that the student belongs to
             * }
             * */
            isFullClass = true;
            const assignmentSectionStudents = allStudents
              .filter(
                (student) => student.sectionId === currentAssignmentSectionId,
              )
              .map((student) => ({
                ...student,
                id: getFormIdField(
                  student.studentId,
                  student.sectionId,
                  currentAssignmentId,
                ),
                studentId: student.studentId,
                assignmentId: currentAssignmentId,
                status: false,
              }));
            formStudentsWithAllInfo = formStudentsWithAllInfo.concat(
              assignmentSectionStudents,
            );
          }

          if (isFullClass) {
            /* Since all the students within a class are added to the form, we should also add the class. */
            allAssigneesList.push({
              id: getFormIdField(
                currentAssignmentSectionId,
                currentAssignmentId,
              ),
              sectionId,
              name: section_name,
              assignmentId: currentAssignmentId,
            });
          }
        });

        // Check students from other uncompleted ISAs
        const { sub_group_students: subGroupStudents } = assignment;
        const sectionStudents = buildSectionStudents(
          get(studentsBySection, [parseInt(assignment.section_id, 10)]),
          subGroupStudents,
        );
        const isISAAssignment = (subGroupStudents || []).length > 0;
        let otherIsaStudents = [];

        if (isISAAssignment) {
          // Get all other associated ISAs that are unassigned. Those students contribute to disabled students.
          const associatedUncompletedISAs = assignments
            .filter((a) => {
              const finishedPercentage = getFinishedPercent(a.progress);
              const isCurrentAssignment = a.id === assignmentId;
              const isCurrentSectionAssignment = a.section_id === sectionId;
              const isISA = (a.sub_group_students || []).length > 0;
              const isCompletedAssignment =
                a.status === 'closed' || finishedPercentage === 100;
              return (
                !isCurrentAssignment &&
                isCurrentSectionAssignment &&
                isISA &&
                !isCompletedAssignment
              );
            })
            .map((a) => ({
              id: a.id,
              sectionStudents: buildSectionStudents(
                get(studentsBySection, [parseInt(a.section_id, 10)]),
                a.sub_group_students,
              ),
              sectionId: a.section_id,
            }));

          associatedUncompletedISAs.push({
            id: assignmentId,
            sectionStudents,
            sectionId,
          });

          associatedUncompletedISAs.forEach((isa) => {
            const otherUncompletedISAs = associatedUncompletedISAs.filter(
              (a) => a.id !== isa.id,
            );
            otherUncompletedISAs.forEach((otherIsa) => {
              otherIsaStudents = otherIsaStudents.concat(
                otherIsa.sectionStudents.map((student) => ({
                  ...student,
                  studentId: student.user_id,
                  sectionId: otherIsa.sectionId,
                  assignmentId: isa.id,
                })),
              );
            });
          });
        }

        if (otherIsaStudents.length > 0) {
          let formStudentsFromOtherISAs = [];
          otherIsaStudents.forEach((otherIsaStudent) => {
            const student = allStudents.find(
              (sectionStudent) =>
                sectionStudent.studentId ===
                  otherIsaStudent.studentId.toString() &&
                sectionStudent.sectionId ===
                  otherIsaStudent.sectionId.toString(),
            );

            if (student) {
              formStudentsFromOtherISAs.push({
                ...student,
                id: getFormIdField(
                  student.studentId,
                  student.sectionId,
                  otherIsaStudent.assignmentId,
                ),
                assignmentId: otherIsaStudent.assignmentId,
                status: true,
                fromOtherISA: true,
              });
            }
          });

          if (formStudentsFromOtherISAs.length > 0) {
            formStudentsWithAllInfo = formStudentsWithAllInfo.concat(
              formStudentsFromOtherISAs,
            );
          }
        }

        setDisabledStudents(
          formStudentsWithAllInfo.filter((student) => student.status === true),
        );
        initialFormStudents.current = formStudentsWithAllInfo;
      }
    }

    const {
      due_at,
      print_toggle,
      score_display,
      related_assignments: relatedAssignments,
      student_scoring,
      timer: initialTimer,
      is_secure,
      test_taking_location,
      duration: initialDuration,
      timezone,
      has_immediate_feedback: hasImmediateFeedback,
      immediate_feedback_attempts: immediateFeedbackAttempts,
      allow_late_submission: allowLateSubmission,
      is_hard_timer: isHardTimer,
    } = assignment;
    // The following block of code might seem crazy, but it is covering the case of
    // updating a composed assignment (aka Final Exam).
    // Its will get the dates according to the order of the assessmentGroup
    // if its not a composed Final Exam, it will use the assignment date as it is.
    // Example: if the assignment to update is the last assignment of an assessment group
    //          then its date is the starts_at_part_2, not the starts_at. This code make the switch
    const isTwoPartsFinalExam = isFinalExam && !!relatedAssignments.length;
    const assignmentDates = getAssignmentDates(
      assignment,
      {
        startsAt: assignment.starts_at,
        startsAtPart2: isTwoPartsFinalExam
          ? relatedAssignments[0].starts_at
          : null,
      },
      isFinalExam,
      assessmentGroupInfo ? assessmentGroupInfo.members : [],
    );

    let assignmentStartDate =
      assignmentDates.startsAt &&
      dayjs(assignmentDates.startsAt).tz(timezone).startOf('minute');
    let assignmentDueDate =
      due_at && dayjs(due_at).tz(timezone).startOf('minute');

    if (!isFinalExam) {
      if (assignmentStartDate) {
        assignmentStartDate = assignmentStartDate.utcOffset(0, true);
      }

      if (assignmentDueDate) {
        assignmentDueDate = assignmentDueDate.utcOffset(0, true);
      }
    }

    initialFormValues.current = {
      assignmentId: assignment.id.toString(),
      startsAt: assignmentStartDate,
      startsAtPart2: isTwoPartsFinalExam
        ? dayjs(assignmentDates.startsAtPart2).tz(timezone).startOf('minute')
        : null,
      partType: isTwoPartsFinalExam ? PartType.PARTIAL : PartType.FULL,
      dueAt: assignmentDueDate,
      timer: initialTimer && Number.parseInt(initialTimer, 10),
      printToggle: print_toggle,
      scoreDisplay: score_display,
      studentScoring: student_scoring,
      isSecure: is_secure,
      duration: initialDuration,
      assessmentGroup: assessmentGroupInfo,
      testTakingLocation: test_taking_location,
      assignees: [],
      allAssignees: [],
      students: [],
      hasImmediateFeedback,
      immediateFeedbackAttempts: Number.isNaN(immediateFeedbackAttempts)
        ? '0'
        : String(immediateFeedbackAttempts),
      allowLateSubmission,
      isSoftTimer: allowHardTimer && isHardTimer ? false : true,
    };

    if (!isFinalExam && individualStudentAssignmentFlag) {
      /* We should use the fetched data to init the form only once. Otherwise we're using existing form data. */
      const formAssignees = wasFormInitialized
        ? allAssignees
        : allAssigneesList;
      const formStudents = wasFormInitialized
        ? propFormStudents
        : formStudentsWithAllInfo;

      initialFormValues.current = {
        ...initialFormValues.current,
        assignees: formAssignees,
        allAssignees: formAssignees,
        students: formStudents,
      };

      calculateClassesStats({
        dispatch: dispatchClassesStats,
        stats: classesStats,
        students: formStudents,
      });
    }
  } else {
    // new assignment
    const printToggleValue = false;
    let defaultAssignees = [];
    let defaultUpdateAssignees = [];
    let defaultAllAssignees = [];

    if (autoSelectedGroups.current.length) {
      defaultUpdateAssignees = autoSelectedGroups.current;
      defaultAllAssignees = autoSelectedGroups.current;
    } else if (!isFinalExam) {
      defaultAssignees = regularSections;
      defaultUpdateAssignees = updateSections;
      defaultAllAssignees = regularSections.concat(updateSections);
      if (progressSections.length && defaultAllAssignees.length > 1) {
        defaultAssignees = [];
        defaultUpdateAssignees = [];
        defaultAllAssignees = [];
      }
    }

    //When reassigning there is one defaultAssignees and multiple groups (used for sectionsMinTimezone)
    let timezone;
    if (defaultAssignees.length === 1) {
      timezone = defaultAssignees[0].timezone || defaultTimezone;
    } else {
      timezone = sectionsMinTimezone;
    }

    const allAssigneesStudents = getStudentsBySections(
      subjectClasses,
      defaultAllAssignees,
    );
    const disabledStudentFormIds = disabledStudents.map(
      (student) => student.id,
    );
    const assignDefaultStudents = allAssigneesStudents.filter(
      (student) => !disabledStudentFormIds.includes(student.id),
    );

    calculateClassesStats({
      dispatch: dispatchClassesStats,
      stats: classesStats,
      students: assignDefaultStudents,
    });

    const setIsSecureFalseOnPurpose = true; // more details in FY-20224
    initialFormValues.current = {
      assignees: !isFinalExam ? defaultAssignees : [],
      updateAssignees: defaultUpdateAssignees,
      allAssignees: defaultAllAssignees,
      startsAt: getNowMinTimezoneTime(
        isFinalExam,
        timezone,
        assessmentGroupInfo,
      ),
      startsAtPart2: null,
      partType: PartType.FULL,
      dueAt: null,
      printToggle: printToggleValue,
      timer: isFinalExam ? PartType.FULL.duration(isPilot) : null,
      scoreDisplay: 'full',
      hasScrambledOrder: false,
      studentScoring: false,
      title: '',
      isSecure: isFinalExam
        ? false
        : hasAudioRecordOrFileUpload
          ? false
          : setIsSecureFalseOnPurpose
            ? false
            : shouldApplyRestriction && isSecureAssessmentMandatory,
      hasImmediateFeedback: false,
      immediateFeedbackAttempts: '0',
      duration: isFinalExam ? PartType.FULL.duration(isPilot) : null,
      attestationAgreementChecked: acceptedPracticeExamToc || false,
      assessmentGroup: assessmentGroupInfo || null,
      students: assignDefaultStudents,
      testTakingLocation: testTakingLocation || null,
    };

    calculateClassesStats({
      dispatch: dispatchClassesStats,
      stats: classesStats,
      students: assignDefaultStudents,
    });
  }

  initialize(initialFormValues.current);
};

export default initAssessmentForm;
